import { useSetState } from 'ahooks';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { BaseLineLoading2 } from '@/app/components/icons/BaseLineLoading2';
import { type FollowResult, useFollow } from '@/app/components/shared/Community';
import { FollowButton } from '@/app/components/shared/FollowButton';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch, useGoToProfile, useIsScreen } from '@/app/hooks';
import { cn } from '@/app/utils';
import { apiGetMyFollowers, apiGetMyFollowings } from '../apis';
import { PAGE_SIZE, STAT_MODAL_CONFIGS } from '../constant';
import type { ViduV1FollowerUser, ViduV1FollowingUser, ViduV1UserStat } from '../types';

interface StatModalProps {
  onClose: () => void;
  selectedStatKey?: keyof ViduV1UserStat;
  onFollowChange?: (result: FollowResult) => void;
}

const UserItem = ({
  user,
  onFollowChange,
}: {
  user: ViduV1FollowerUser | ViduV1FollowingUser;
  onFollowChange: (result: FollowResult) => void;
}) => {
  const t = useTranslations();
  const { goToProfile } = useGoToProfile();

  const { state, handleFollow } = useFollow(user.user?.user_id || '', {
    initialIsFollowing: user.user?.is_following || false,
    initialIsMutual: user.is_mutual || false,
    onFollowChange: (result) => {
      onFollowChange(result);
    },
  });

  if (!user.user) return null;

  const isDeleted = user.user.is_deleted;

  return (
    <div className="mb-6 flex h-[46px] items-center justify-between gap-3 py-[3px] last:mb-0">
      <div
        className={cn('flex min-w-0 items-center gap-3', !isDeleted && 'cursor-pointer')}
        onClick={() => !isDeleted && goToProfile(user.user?.user_id)}
      >
        <img
          src={user.user.avatar || ''}
          alt="avatar"
          className="size-10 flex-shrink-0 rounded-full bg-system-white10 object-cover"
        />
        <div className="min-w-0 truncate font-semibold">
          {isDeleted ? t('profile.logout.tip') : user.user.nick_name}
        </div>
      </div>
      {!isDeleted && <FollowButton isFollowing={state.isFollowing} isMutual={state.isMutual} onClick={handleFollow} />}
    </div>
  );
};

type StatModalState = {
  activeTab?: keyof ViduV1UserStat;
  users: (ViduV1FollowerUser | ViduV1FollowingUser)[];
};

export const StatModal = ({ onClose, selectedStatKey, onFollowChange: onExternalFollowChange }: StatModalProps) => {
  const t = useTranslations();
  const { isSmScreen } = useIsScreen();

  const [state, setState] = useSetState<StatModalState>({
    activeTab: selectedStatKey,
    users: [],
  });

  const pageToken = useRef<string>('');

  const { loading: followersLoading, run: fetchFollowers } = useFetch(
    () =>
      apiGetMyFollowers({
        'pager.page_token': pageToken.current,
        'pager.pagesz': PAGE_SIZE,
      }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          toast({
            type: 'warning',
            message: error.message,
          });
        }
        if (data) {
          setState((prev) => ({ users: [...prev.users, ...(data?.follower_list || [])] }));
          pageToken.current = data?.next_page_token || '';
        }
      },
    },
  );

  const { loading: followingsLoading, run: fetchFollowings } = useFetch(
    () =>
      apiGetMyFollowings({
        'pager.page_token': pageToken.current,
        'pager.pagesz': PAGE_SIZE,
      }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          toast({
            type: 'warning',
            message: error.message,
          });
        }
        if (data) {
          setState((prev) => ({ users: [...prev.users, ...(data?.following_list || [])] }));
          pageToken.current = data?.next_page_token || '';
        }
      },
    },
  );

  useEffect(() => {
    if (state.activeTab === 'follower_count') {
      pageToken.current = '';
      setState({ users: [] });
      fetchFollowers();
    } else if (state.activeTab === 'following_count') {
      pageToken.current = '';
      setState({ users: [] });
      fetchFollowings();
    }
  }, [state.activeTab]);

  const handleTabChange = (tab: keyof ViduV1UserStat | undefined) => {
    setState({ activeTab: tab });
    if (tab === 'follower_count') {
      pageToken.current = '';
      setState({ users: [] });
      fetchFollowers();
    } else if (tab === 'following_count') {
      pageToken.current = '';
      setState({ users: [] });
      fetchFollowings();
    }
  };

  const handleFollowChange = (result: FollowResult) => {
    // 在粉丝列表中，对方一定是关注了我们：
    //   -如果是互相关注，我们取关后再回关还是互关状态
    //   -如果我们回关对方，变成互相关注状态
    if (state.activeTab === 'follower_count') {
      setState({
        users: state.users.map((follower) =>
          follower.user?.user_id === result.targetUid
            ? {
                ...follower,
                user: follower.user ? { ...follower.user, is_following: result.followed } : undefined,
                is_mutual: result.followed,
              }
            : follower,
        ),
      });
    } else if (state.activeTab === 'following_count') {
      // 在关注列表中 我们一定是关注了对方：
      //   -如果是互相关注，我们取关后再回关还是互关状态
      //   -如果我们取关对方，再回关还是关注状态
      setState({
        users: state.users.map((following) =>
          following.user?.user_id === result.targetUid
            ? {
                ...following,
                user: following.user ? { ...following.user, is_following: result.followed } : undefined,
                is_mutual: result.followed ? following.is_mutual : false,
              }
            : following,
        ),
      });
    }

    // 调用外部传入的回调，更新 Profile 中的统计数据
    onExternalFollowChange?.(result);
  };

  const isLoading = followersLoading || followingsLoading;
  const hasMore = !!pageToken.current;

  const loadMore = () => {
    if (isLoading || !hasMore) return;

    if (state.activeTab === 'follower_count') {
      fetchFollowers();
    } else if (state.activeTab === 'following_count') {
      fetchFollowings();
    }
  };

  const renderTabButtons = () => (
    <>
      {STAT_MODAL_CONFIGS.map((config) => (
        <button
          key={config.value}
          className={cn(
            'after:-bottom-1 relative font-semibold text-base text-system-white64 transition-colors after:absolute after:left-0 after:h-[2px] after:w-full after:rounded-full',
            state.activeTab === config.value
              ? 'text-white after:bg-white sm:after:bg-ShengshuButton'
              : 'text-system-white64 after:bg-transparent',
          )}
          onClick={() => handleTabChange(config.value)}
        >
          {t(config.label)}
        </button>
      ))}
    </>
  );

  const renderContent = () => {
    if (isLoading && state.users.length === 0) {
      return (
        <div className="relative flex h-full flex-col items-center justify-center">
          <BaseLineLoading2 className="animate-spin text-[32px]" />
        </div>
      );
    }

    if (!isLoading && state.users.length === 0) {
      return (
        <div className="flex h-full flex-col items-center justify-center">
          <div className="text-system-white64">{t('profile.noFollowing')}</div>
        </div>
      );
    }

    return (
      <div className="hide-scrollbar h-full overflow-y-auto" id="scrollableTarget">
        <InfiniteScroll
          dataLength={state.users.length}
          next={loadMore}
          hasMore={hasMore}
          loader={
            <div className="flex items-center justify-center">
              <BaseLineLoading2 className="animate-spin text-[32px]" />
            </div>
          }
          scrollThreshold={0.8}
          scrollableTarget="scrollableTarget"
        >
          {state.users.map((item) => (
            <UserItem key={item.user?.user_id ?? ''} user={item} onFollowChange={handleFollowChange} />
          ))}
        </InfiniteScroll>
      </div>
    );
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      {/* 移动端全屏显示 */}
      {isSmScreen && (
        <DialogContent
          className="flex flex-col gap-0 bg-system-bg08 p-0 text-system-white sm:gap-0"
          variant="fullScreen"
          closable={false}
        >
          <div className="flex h-11 items-center">
            <BaseLineLeft className="absolute text-2xl text-system-white" onClick={() => onClose()} />
            <div className="flex flex-1 items-center justify-center gap-7">{renderTabButtons()}</div>
          </div>

          <div className="mt-4 h-[calc(100vh-44px)] w-full flex-1 overflow-hidden">{renderContent()}</div>
        </DialogContent>
      )}

      {/* PC端弹窗显示 */}
      {!isSmScreen && (
        <DialogContent className="w-[620px] gap-0">
          <div className="flex items-center gap-6">{renderTabButtons()}</div>

          <div className="mt-[33px] h-[536px] w-full overflow-hidden">{renderContent()}</div>
        </DialogContent>
      )}
    </Dialog>
  );
};
